import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { backlogIssueUrl, formatPt } from './format';
import type { OutputHygieneItem, OutputWip } from '@/lib/api';

interface HygieneSectionProps {
  wip: OutputWip;
  releasedDateMissing: OutputHygieneItem[];
  /** リリース完了日が誤入力の案件。件数が少ないので一覧ではなくキーだけ並べる */
  invalidReleaseDates: OutputHygieneItem[];
}

/** @graph-connects none */
function IssueRow({ item }: { item: OutputHygieneItem }) {
  return (
    <tr style={{ borderTop: '1px solid var(--color-border)' }}>
      <td className="py-2 pr-3 align-top whitespace-nowrap">
        <a
          href={backlogIssueUrl(item.issueKey)}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium underline"
          style={{ color: 'var(--color-primary)' }}
        >
          {item.issueKey}
        </a>
      </td>
      <td className="py-2 pr-3 align-top text-xs" style={{ color: 'var(--color-text-primary)' }}>
        {item.summary}
      </td>
      <td
        className="py-2 pr-3 align-top text-xs tabular-nums"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {formatPt(item.pt)}
      </td>
      <td
        className="py-2 pr-3 align-top text-xs tabular-nums"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {item.daysSinceUpdate ?? '—'}
      </td>
      <td className="py-2 align-top">
        <div className="flex flex-wrap gap-1">
          {item.missingFields.map((field) => (
            <Badge key={field} variant="warning" size="sm">
              {field}
            </Badge>
          ))}
        </div>
      </td>
    </tr>
  );
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 必須項目の記入漏れを表示する。仕掛かりは全件が着手中のため、Backlogの状態ではなく記入の追いつき具合を詰まりの指標として扱う
 * @graph-connects none
 */
export function HygieneSection({
  wip,
  releasedDateMissing,
  invalidReleaseDates,
}: HygieneSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <GlassCard className="lg:col-span-1">
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          記入漏れの内訳（仕掛かり{wip.count}件）
        </p>
        <ul className="mt-3 space-y-2">
          {wip.byField.map((row) => (
            <li key={row.field} className="flex items-center justify-between">
              <span className="text-xs" style={{ color: 'var(--color-text-primary)' }}>
                {row.field}
              </span>
              <span
                className="text-sm font-semibold tabular-nums"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {row.count}件
              </span>
            </li>
          ))}
          {wip.byField.length === 0 && (
            <li className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              未入力はありません。
            </li>
          )}
        </ul>
        {wip.onHoldCount > 0 && (
          <p className="mt-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            うち{wip.onHoldCount}件は意図的な保留のため、上の件数には数えていません。
          </p>
        )}
        <p className="mt-4 text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
          仕掛かりは全件が着手中の案件です。Ptが未見積もり（空欄と0）の{wip.ptMissingCount}件は
          残量に含まれないため、表示されている{wip.totalPt.toLocaleString()}
          ptは実際より小さく出ます。
        </p>
      </GlassCard>

      <GlassCard className="lg:col-span-2">
        <div className="mb-2 flex items-center gap-2">
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            リリース完了日の記入漏れ
          </p>
          <Badge variant="error" size="sm">
            {releasedDateMissing.length}件
          </Badge>
        </div>
        <p className="mb-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          状態が完了なのにリリース完了日が空の案件です。このままだと上のリリース数・Ptから落ち続けます。
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left">
            <thead>
              <tr>
                <th
                  className="whitespace-nowrap pb-2 pr-3 text-xs font-medium"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  キー
                </th>
                <th
                  className="whitespace-nowrap pb-2 pr-3 text-xs font-medium"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  案件名
                </th>
                <th
                  className="whitespace-nowrap pb-2 pr-3 text-xs font-medium"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Pt
                </th>
                <th
                  className="whitespace-nowrap pb-2 pr-3 text-xs font-medium"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  更新なし日数
                </th>
                <th
                  className="whitespace-nowrap pb-2 text-xs font-medium"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  未入力
                </th>
              </tr>
            </thead>
            <tbody>
              {releasedDateMissing.map((item) => (
                <IssueRow key={item.issueKey} item={item} />
              ))}
            </tbody>
          </table>
          {releasedDateMissing.length === 0 && (
            <p className="py-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              記入漏れはありません。
            </p>
          )}
        </div>
        {invalidReleaseDates.length > 0 && (
          <p className="mt-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            リリース完了日が誤入力（未来日・極端に古い日）の案件が{invalidReleaseDates.length}
            件あり、 集計から除いています:{' '}
            {invalidReleaseDates.map((item) => (
              <a
                key={item.issueKey}
                href={backlogIssueUrl(item.issueKey)}
                target="_blank"
                rel="noreferrer"
                className="mr-2 underline"
                style={{ color: 'var(--color-primary)' }}
              >
                {item.issueKey}
              </a>
            ))}
          </p>
        )}
      </GlassCard>
    </div>
  );
}
